import type { PersistedToken } from '@app/api/token';
import type { CursorListResponse } from '@app/types/org-console';
import { ApiResponseError, buildOrgUrl } from '@app/utils/api';
import { createTimedFetch } from '@app/utils/timed-fetch';

type TokenResolver = (name?: string, scopes?: string[]) => string | Promise<string | undefined> | undefined;

type RefreshTokens = () => Promise<PersistedToken | null>;

interface AuthFetchOptions {
  resolveAccessToken: TokenResolver;
  refreshTokens: RefreshTokens;
}

export const createAuthFetch = ({ resolveAccessToken, refreshTokens }: AuthFetchOptions) => {
  const timedFetch = createTimedFetch();

  const fetchWithAuthRetry = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const headers = new Headers(init.headers ?? undefined);
    if (!headers.has('Authorization')) {
      const token = await Promise.resolve(resolveAccessToken());
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    }

    const response = await timedFetch(url, { ...init, headers });

    if (response.status !== 401) {
      return response;
    }

    const refreshed = await refreshTokens();
    if (!refreshed?.accessToken) {
      return response;
    }

    const retryHeaders = new Headers(headers);
    retryHeaders.set('Authorization', `Bearer ${refreshed.accessToken}`);
    return timedFetch(url, { ...init, headers: retryHeaders });
  };

  const fetchJson = async <T>(url: string, init?: RequestInit): Promise<T> => {
    const headers = new Headers(init?.headers ?? undefined);
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }

    const response = await fetchWithAuthRetry(url, { method: 'GET', ...init, headers });

    if (!response.ok) {
      throw new ApiResponseError(response, `Request failed with status ${response.status}`);
    }

    const contentType = response.headers.get('Content-Type') ?? '';
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new ApiResponseError(response, 'Expected JSON response but received a different content type.');
    }

    return (await response.json()) as T;
  };

  const fetchOrgCollection = async <T>(
    basePath: string,
    orgId: string,
    resource: string,
    init?: RequestInit
  ): Promise<CursorListResponse<T>> => {
    const url = buildOrgUrl(basePath, orgId, resource);
    return fetchJson<CursorListResponse<T>>(url, init);
  };

  const fetchOrgResource = async <T>(
    basePath: string,
    orgId: string,
    resource: string = '',
    init?: RequestInit
  ): Promise<T> => {
    const url = buildOrgUrl(basePath, orgId, resource);
    return fetchJson<T>(url, init);
  };

  return {
    fetchWithAuthRetry,
    fetchJson,
    fetchOrgCollection,
    fetchOrgResource
  };
};
